/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import moment from 'moment'
import {
  DragAndDrop,
  Inject,
  Month,
  PopupOpenEventArgs,
  RenderCellEventArgs,
  Resize,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
} from '@syncfusion/ej2-react-schedule'
import { IAppointment } from '@dataTypes/appointment.type'
import './MonthCalendar.style.css'
import {
  CellTemplate,
  groupAppointmentsByDateAndProvider,
} from './MonthCalendarTemplate'
import {
  INITIAL_SCHEDULER_SETTINGS_DATA,
  SchedulerSettingsType,
} from '@components/pages/Settings/System/Scheduler/Scheduler.component'

type MonthCalendarProps = {
  selectDate: string | Date
  appointmentList: IAppointment[]
  onViewLoaded?: () => void
  heightOffset?: number
  schedulerSettings?: SchedulerSettingsType
}

const VIEW_LOADED_DELAY_MS = 100
const HEADER_LAYOUT_BREAKPOINT_PX = 1133

const INJECT_SERVICES = [Month, Resize, DragAndDrop] as const

function applyResponsiveHeaderLayout(): void {
  const rowCalendarHeaderElement = document.getElementById(
    'calendar-header-row-top',
  )
  const calendarHeaderWidth = rowCalendarHeaderElement?.offsetWidth
  const calendarContentRowElement = document.getElementById(
    'calendar-content-row',
  )

  if (!calendarContentRowElement) return

  if (
    calendarHeaderWidth &&
    calendarHeaderWidth < HEADER_LAYOUT_BREAKPOINT_PX
  ) {
    if (rowCalendarHeaderElement?.firstElementChild instanceof HTMLElement) {
      rowCalendarHeaderElement.firstElementChild.style.setProperty(
        'margin-bottom',
        '4px',
        'important',
      )
    }
    return
  }

  calendarContentRowElement.style.removeProperty('margin-top')
  if (rowCalendarHeaderElement?.firstElementChild instanceof HTMLElement) {
    rowCalendarHeaderElement.firstElementChild.style.removeProperty(
      'margin-bottom',
    )
  }
}

function MonthCalendar({
  selectDate,
  appointmentList,
  onViewLoaded,
  heightOffset = 10,
  schedulerSettings = INITIAL_SCHEDULER_SETTINGS_DATA,
}: MonthCalendarProps): React.ReactElement {
  const scheduleObj = useRef<ScheduleComponent>(null)

  const [height, setHeight] = useState(0)

  const onViewLoadedRef = useRef(onViewLoaded)
  onViewLoadedRef.current = onViewLoaded

  const viewLoadedCalledRef = useRef(false)
  const headerAbbreviatedRef = useRef(false)

  const safeAppointmentList = appointmentList ?? []

  const scheduleIsReady = height > 0
  const scheduleHeight = scheduleIsReady ? height : window.innerHeight - 146

  const selectedDate = useMemo(() => {
    const parsed = moment(selectDate, 'MMMM D, YYYY')
    return parsed.isValid() ? parsed.toDate() : new Date(selectDate)
  }, [selectDate])

  const selectDateMoment = useMemo(
    () =>
      moment(selectDate, 'MMMM D, YYYY').isValid()
        ? moment(selectDate, 'MMMM D, YYYY')
        : moment(selectDate),
    [selectDate],
  )

  const categorizedAppointments = useMemo(
    () => groupAppointmentsByDateAndProvider(safeAppointmentList),
    [safeAppointmentList],
  )

  const categorizedAppointmentsRef = useRef(categorizedAppointments)
  categorizedAppointmentsRef.current = categorizedAppointments

  useEffect(() => {
    const stage = document.querySelector('.appt-calendar-stage')
    const apptContainer = document.querySelector('.appt-container')
    const element =
      stage instanceof HTMLElement
        ? stage
        : apptContainer instanceof HTMLElement
          ? apptContainer
          : null

    if (!element) return

    const measureHeight = (): void => {
      const nextHeight =
        Math.round(element.getBoundingClientRect().height) - 56 - heightOffset
      setHeight((prev) => (prev !== nextHeight ? nextHeight : prev))
    }

    measureHeight()

    const observer = new ResizeObserver(() => {
      measureHeight()
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [heightOffset])

  // Responsive header layout (mount + resize)
  useEffect(() => {
    let layoutTimeoutId: ReturnType<typeof setTimeout> | null = null

    const scheduleLayout = (): void => {
      if (layoutTimeoutId) clearTimeout(layoutTimeoutId)
      layoutTimeoutId = setTimeout(() => {
        applyResponsiveHeaderLayout()
      }, 100)
    }

    scheduleLayout()
    window.addEventListener('resize', scheduleLayout)

    return () => {
      if (layoutTimeoutId) clearTimeout(layoutTimeoutId)
      window.removeEventListener('resize', scheduleLayout)
    }
  }, [])

  useEffect(() => {
    viewLoadedCalledRef.current = false
    headerAbbreviatedRef.current = false
  }, [selectDate, safeAppointmentList])

  const notifyViewLoaded = useCallback(() => {
    if (viewLoadedCalledRef.current) return
    viewLoadedCalledRef.current = true
    onViewLoadedRef.current?.()
  }, [])

  const handlePopupOpen = useCallback((args: PopupOpenEventArgs) => {
    if (args.type === 'Editor' || args.type === 'QuickInfo') {
      args.cancel = true
    }
  }, [])

  const handleRenderCell = useCallback(
    (args: RenderCellEventArgs) => {
      if (args.elementType !== 'monthCells' || !args.element || !args.date) {
        return
      }

      const cellDate = moment(args.date).format('YYYY-MM-DD')
      const today = moment().format('YYYY-MM-DD')
      const currentMonth = selectDateMoment.month()
      const currentYear = selectDateMoment.year()
      const cellMonth = moment(args.date).month()
      const cellYear = moment(args.date).year()

      if (cellDate === today) {
        args.element.classList.add('current-date-cell')
      }

      if (cellMonth !== currentMonth || cellYear !== currentYear) {
        args.element.classList.add('other-month-cell')
      }
    },
    [selectDateMoment],
  )

  const cellTemplate = useCallback(
    (props: any) => CellTemplate(props, categorizedAppointmentsRef.current),
    [],
  )

  const abbreviateMonthHeaders = useCallback(() => {
    if (!scheduleObj.current || scheduleObj.current.currentView !== 'Month') {
      return
    }

    const dayHeaderElements = document.querySelectorAll(
      '.month-calendar-container .e-month-view .e-header-cells',
    )

    dayHeaderElements.forEach((header) => {
      const fullDayName = header.textContent
      if (!fullDayName) return

      const abbreviated = moment(fullDayName, 'dddd').format('ddd')
      if (abbreviated !== 'Invalid date') {
        header.textContent = abbreviated
        header.classList.add('abbreviated-header')
      }
    })

    headerAbbreviatedRef.current = true
  }, [])

  const handleDataBound = useCallback(() => {
    if (!headerAbbreviatedRef.current) {
      abbreviateMonthHeaders()
    }

    window.setTimeout(() => {
      notifyViewLoaded()
    }, VIEW_LOADED_DELAY_MS)
  }, [abbreviateMonthHeaders, notifyViewLoaded])

  return (
    <div
      className={`month-calendar-container ${schedulerSettings.view_mode === 'Full' ? 'full' : ''}`}
    >
      <ScheduleComponent
        ref={scheduleObj}
        cssClass="month-view"
        width="100%"
        height={`${scheduleHeight}px`}
        selectedDate={selectedDate}
        popupOpen={handlePopupOpen}
        allowKeyboardInteraction={false}
        cellTemplate={cellTemplate}
        renderCell={handleRenderCell}
        dataBound={handleDataBound}
        workDays={[0, 1, 2, 3, 4, 5, 6]}
        firstDayOfWeek={0}
      >
        <ViewsDirective>
          <ViewDirective option="Month" isSelected />
        </ViewsDirective>
        <Inject services={[...INJECT_SERVICES]} />
      </ScheduleComponent>
    </div>
  )
}

function areMonthCalendarPropsEqual(
  prev: MonthCalendarProps,
  next: MonthCalendarProps,
): boolean {
  return (
    prev.selectDate === next.selectDate &&
    prev.appointmentList === next.appointmentList &&
    prev.heightOffset === next.heightOffset &&
    prev.onViewLoaded === next.onViewLoaded
  )
}

export default memo(MonthCalendar, areMonthCalendarPropsEqual)
